import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';
import { playNotificationSound } from '../helpers/notificationSound';
import { alertarErro } from '../helpers/alerts';

const DIAS = [
  { num: 0, label: 'Dom' },
  { num: 1, label: 'Seg' },
  { num: 2, label: 'Ter' },
  { num: 3, label: 'Qua' },
  { num: 4, label: 'Qui' },
  { num: 5, label: 'Sex' },
  { num: 6, label: 'Sáb' },
];

const SONS_NOVA_MENSAGEM = [
  { chave: 'suave', label: 'Suave' },
  { chave: 'alerta', label: 'Alerta' },
  { chave: 'discreto', label: 'Discreto' },
];

const PLACEHOLDERS = {
  mensagem_saudacao: 'Oi! Aqui é o atendimento do Centrão. Como posso te ajudar?',
  mensagem_fora_horario: 'Oi! No momento estamos fora do horário de atendimento. Sua mensagem foi recebida - retornaremos no próximo horário útil.',
  mensagem_cancelamento: 'Tudo bem, descartei o registro. Quando precisar de novo, é só mandar uma mensagem.',
  mensagem_chamado_criado: 'Pronto! Seu chamado foi registrado com o número *#{{numero}}*. Um atendente vai analisar em breve.',
};

function minutosParaTexto(min) {
  if (!min || min < 1) return '';
  if (min < 60) return `= ${min} min`;
  if (min < 1440) return `= ${(min / 60).toFixed(min % 60 === 0 ? 0 : 1)}h`;
  return `= ${(min / 1440).toFixed(min % 1440 === 0 ? 0 : 1)} dias`;
}

export default function AdminAtendimento() {
  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('admin:atendimento');

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [motivos, setMotivos] = useState([]);
  const [motivosErro, setMotivosErro] = useState('');
  const [modoModal, setModoModal] = useState({ aberto: false, editando: null });
  const [modoNome, setModoNome] = useState('');
  const [modoMensagem, setModoMensagem] = useState('');
  const [modoSalvando, setModoSalvando] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [categoriasErro, setCategoriasErro] = useState('');
  const [filiais, setFiliais] = useState([]);
  const [filiaisErro, setFiliaisErro] = useState('');
  const [setores, setSetores] = useState([]);
  const [setoresErro, setSetoresErro] = useState('');
  const [filas, setFilas] = useState([]); // fio_setor (fila real) + modo de trabalho
  const [filaSalvando, setFilaSalvando] = useState(null); // id da fila salvando
  // Funções de fila (caixa/crediário/vendas) + as ferramentas que cada uma libera.
  const [funcoes, setFuncoes] = useState([]);
  const [permsFuncao, setPermsFuncao] = useState([]); // só as que podem vir de função
  const [funcaoSalvando, setFuncaoSalvando] = useState(null);
  const [canais, setCanais] = useState([]); // canais (números) — a fila pertence a um

  useEffect(() => {
    if (!podeEditar) {
      setLoading(false);
      return;
    }
    // Cargas independentes - uma falhar nao deve esconder a outra
    Promise.allSettled([
      axios.get(`${API}/atendimento/config`),
      axios.get(`${API}/atendimento/motivos`),
      axios.get(`${API}/atendimento/categorias`),
      axios.get(`${API}/atendimento/filiais`),
      axios.get(`${API}/atendimento/setores`),
      axios.get(`${API}/setores?ativos=true`),
      axios.get(`${API}/atendimento/canais`),
      axios.get(`${API}/funcoes`),
    ])
      .then(([resConfig, resMotivos, resCategorias, resFiliais, resSetores, resFilas, resCanais, resFuncoes]) => {
        if (resFilas.status === 'fulfilled') setFilas(resFilas.value.data?.data || []);
        if (resCanais.status === 'fulfilled') setCanais(resCanais.value.data?.data || []);
        if (resFuncoes.status === 'fulfilled') {
          setFuncoes(resFuncoes.value.data?.data?.funcoes || []);
          setPermsFuncao(resFuncoes.value.data?.data?.permissoes || []);
        }
        if (resConfig.status === 'fulfilled') {
          setConfig(resConfig.value.data?.data || null);
        } else {
          setErro('Não foi possível carregar a configuração.');
        }
        if (resMotivos.status === 'fulfilled') {
          setMotivos(resMotivos.value.data?.data || []);
          setMotivosErro('');
        } else {
          const msg = resMotivos.reason?.response?.data?.message || resMotivos.reason?.message || '';
          // Quando a tabela ainda nao existe, o SQL Server retorna "Invalid object name".
          if (/Invalid object name|siti_atendimento_motivo/i.test(msg)) {
            setMotivosErro('Tabela de motivos não existe. Aplique a migration 011_motivos_encerramento.sql na base.');
          } else {
            setMotivosErro('Não foi possível carregar os motivos de encerramento.');
          }
        }
        if (resCategorias.status === 'fulfilled') {
          setCategorias(resCategorias.value.data?.data || []);
          setCategoriasErro('');
        } else {
          const msg = resCategorias.reason?.response?.data?.message || resCategorias.reason?.message || '';
          if (/Invalid object name|siti_atendimento_categoria/i.test(msg)) {
            setCategoriasErro('Tabela de categorias não existe. Aplique a migration 013_catalogos_atendimento.sql na base.');
          } else {
            setCategoriasErro('Não foi possível carregar as categorias.');
          }
        }
        if (resFiliais.status === 'fulfilled') {
          setFiliais(resFiliais.value.data?.data || []);
          setFiliaisErro('');
        } else {
          const msg = resFiliais.reason?.response?.data?.message || resFiliais.reason?.message || '';
          if (/Invalid object name|dbo\.FILIAIS/i.test(msg)) {
            setFiliaisErro('View dbo.FILIAIS não encontrada em BDCENTRAO. Verifique se a view existe na base de dados.');
          } else {
            setFiliaisErro('Não foi possível carregar as filiais.');
          }
        }
        if (resSetores.status === 'fulfilled') {
          setSetores(resSetores.value.data?.data || []);
          setSetoresErro('');
        } else {
          const msg = resSetores.reason?.response?.data?.message || resSetores.reason?.message || '';
          if (/Invalid object name|siti_atendimento_setor/i.test(msg)) {
            setSetoresErro('Tabela de setores não existe. Aplique a migration 014_filial_codigo_setor.sql na base.');
          } else {
            setSetoresErro('Não foi possível carregar os setores.');
          }
        }
      })
      .finally(() => setLoading(false));
  }, [podeEditar]);

  const atualizarFilaLocal = (id, patch) => setFilas((fs) => fs.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const criarFila = async () => {
    // Fila pertence a um canal (número): canal -> fila -> atendente.
    if (canais.length === 0) {
      Swal.fire({ icon: 'info', title: 'Cadastre um canal antes', text: 'A fila pertence a um número de WhatsApp.' });
      return;
    }
    const opcoesCanal = canais.map((c) => `<option value="${c.id}">${c.nome}</option>`).join('');
    // A função é perguntada JÁ NA CRIAÇÃO de propósito: é aqui que se esquece,
    // e fila sem função é fila cuja equipe não recebe a ferramenta do trabalho
    // dela — sem erro nenhum aparecer depois.
    const opcoesFuncao = ['<option value="">— sem função —</option>']
      .concat(funcoes.map((f) => `<option value="${f.id}">${f.nome}</option>`)).join('');
    const { value: form, isConfirmed } = await Swal.fire({
      title: 'Nova fila',
      html: `<input id="fila-nome" class="swal2-input" placeholder="Nome da fila (ex.: Vendas)" />`
        + `<select id="fila-canal" class="swal2-select" style="width:80%">${opcoesCanal}</select>`
        + `<div style="font-size:0.8rem;opacity:0.7;margin:2px 0 10px">A fila vale só nesse canal. O mesmo nome pode existir em canais diferentes.</div>`
        + `<select id="fila-funcao" class="swal2-select" style="width:80%">${opcoesFuncao}</select>`
        + `<div style="font-size:0.8rem;opacity:0.7;margin-top:2px">O que essa fila faz. É a função — não o nome — que libera as ferramentas de quem trabalha nela.</div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const nome = document.getElementById('fila-nome').value.trim();
        const canal_id = document.getElementById('fila-canal').value;
        const funcao_id = document.getElementById('fila-funcao').value;
        if (nome.length < 2) { Swal.showValidationMessage('Nome muito curto (mín. 2).'); return false; }
        if (!canal_id) { Swal.showValidationMessage('Escolha o canal.'); return false; }
        return { nome, canal_id: Number(canal_id), funcao_id: funcao_id ? Number(funcao_id) : null };
      },
    });
    if (!isConfirmed || !form) return;
    const { nome, canal_id, funcao_id } = form;
    try {
      const r = await axios.post(`${API}/setores`, { nome: nome.trim(), canal_id });
      const nova = { visibilidade: 'colaborativo', distribuicao: 'self', teto_atendente: null, ...(r.data?.data || {}) };
      // A criação não recebe função (o endpoint é o mesmo de sempre); gravamos
      // logo em seguida pelo mesmo caminho que a tela usa pra editar.
      if (funcao_id && nova.id) {
        const rf = await axios.put(`${API}/setores/${nova.id}/modo`, {
          visibilidade: nova.visibilidade,
          distribuicao: nova.distribuicao,
          teto_atendente: nova.teto_atendente,
          funcao_id,
        });
        Object.assign(nova, rf.data?.data || {});
      }
      setFilas((fs) => [...fs, nova]);
      Swal.fire({ icon: 'success', title: 'Fila criada', timer: 1100, showConfirmButton: false });
    } catch (e) { alertarErro(e, 'Não deu pra criar'); }
  };
  // ---- Funções de fila ------------------------------------------------
  const alternarPermFuncao = (funcaoId, chave) => setFuncoes((fs) => fs.map((f) => (f.id !== funcaoId ? f : {
    ...f,
    permissoes: f.permissoes.includes(chave)
      ? f.permissoes.filter((c) => c !== chave)
      : [...f.permissoes, chave],
  })));

  const salvarFuncao = async (funcao) => {
    setFuncaoSalvando(funcao.id);
    try {
      await axios.put(`${API}/funcoes/${funcao.id}/permissoes`, { permissoes: funcao.permissoes });
      Swal.fire({ icon: 'success', title: 'Função atualizada', timer: 1100, showConfirmButton: false });
    } catch (e) {
      alertarErro(e, 'Não deu pra salvar');
    } finally { setFuncaoSalvando(null); }
  };

  const criarFuncao = async () => {
    const { value: nome } = await Swal.fire({
      title: 'Nova função de fila',
      input: 'text',
      inputLabel: 'Nome (ex.: Vendas, Entregas, Pós-venda)',
      inputPlaceholder: 'Nome da função',
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
    });
    if (!nome || nome.trim().length < 2) return;
    try {
      const r = await axios.post(`${API}/funcoes`, { nome: nome.trim() });
      setFuncoes((fs) => [...fs, r.data?.data].filter(Boolean));
    } catch (e) {
      alertarErro(e, 'Não deu pra criar');
    }
  };

  const salvarModoFila = async (fila) => {
    setFilaSalvando(fila.id);
    try {
      const r = await axios.put(`${API}/setores/${fila.id}/modo`, {
        visibilidade: fila.visibilidade,
        distribuicao: fila.distribuicao,
        teto_atendente: fila.teto_atendente,
        funcao_id: fila.funcao_id ?? null,
      });
      atualizarFilaLocal(fila.id, r.data?.data || {});
      Swal.fire({ icon: 'success', title: 'Fila atualizada', timer: 1100, showConfirmButton: false });
    } catch (e) {
      alertarErro(e, 'Não deu pra salvar');
    } finally { setFilaSalvando(null); }
  };

  const recarregarMotivos = async () => {
    try {
      const res = await axios.get(`${API}/atendimento/motivos`);
      setMotivos(res.data?.data || []);
    } catch (e) { /* silencioso */ }
  };

  const recarregarCategorias = async () => {
    try {
      const res = await axios.get(`${API}/atendimento/categorias`);
      setCategorias(res.data?.data || []);
    } catch (e) { /* silencioso */ }
  };



  const recarregarSetores = async () => {
    try {
      const res = await axios.get(`${API}/atendimento/setores`);
      setSetores(res.data?.data || []);
    } catch (e) { /* silencioso */ }
  };

  const abrirNovoModo = () => {
    setModoNome('');
    setModoMensagem('');
    setModoModal({ aberto: true, editando: null });
  };

  const abrirEditarModo = (modo) => {
    setModoNome(modo.descricao || '');
    setModoMensagem(modo.mensagem_cliente || '');
    setModoModal({ aberto: true, editando: modo });
  };

  const fecharModoModal = () => setModoModal({ aberto: false, editando: null });

  const salvarModo = async () => {
    const nome = modoNome.trim();
    if (nome.length < 3) {
      Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Nome precisa ter ao menos 3 caracteres.' });
      return;
    }
    setModoSalvando(true);
    try {
      const body = { descricao: nome, mensagem_cliente: modoMensagem.trim() || null };
      if (modoModal.editando) {
        await axios.put(`${API}/atendimento/motivos/${modoModal.editando.id}`, body);
      } else {
        await axios.post(`${API}/atendimento/motivos`, { ...body, ordem: (motivos.length + 1) * 10 });
      }
      await recarregarMotivos();
      fecharModoModal();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    } finally {
      setModoSalvando(false);
    }
  };

  const toggleAtivoMotivo = async (motivo) => {
    try {
      await axios.put(`${API}/atendimento/motivos/${motivo.id}`, { ativo: !motivo.ativo });
      await recarregarMotivos();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const removerMotivo = async (motivo) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Remover motivo?',
      text: `"${motivo.descricao}" será apagado. Chamados já fechados que usaram esse motivo continuam com o registro.`,
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/atendimento/motivos/${motivo.id}`);
      await recarregarMotivos();
    } catch (err) {
      alertarErro(err, 'Não deu pra excluir');
    }
  };

  const novaCategoria = async () => {
    const { value: nome } = await Swal.fire({
      title: 'Nova categoria',
      input: 'text',
      inputLabel: 'Nome exibido para o usuário',
      inputPlaceholder: 'Ex: Telefonia',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Adicionar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || v.trim().length < 2) && 'Mínimo 2 caracteres.',
    });
    if (!nome) return;
    try {
      await axios.post(`${API}/atendimento/categorias`, {
        nome: nome.trim(),
        ordem: (categorias.length + 1) * 10,
      });
      await recarregarCategorias();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const renomearCategoria = async (categoria) => {
    const { value: nome } = await Swal.fire({
      title: 'Editar categoria',
      input: 'text',
      inputValue: categoria.nome,
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || v.trim().length < 2) && 'Mínimo 2 caracteres.',
    });
    if (!nome || nome.trim() === categoria.nome) return;
    try {
      await axios.put(`${API}/atendimento/categorias/${categoria.id}`, { nome: nome.trim() });
      await recarregarCategorias();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const toggleAtivoCategoria = async (categoria) => {
    try {
      await axios.put(`${API}/atendimento/categorias/${categoria.id}`, { ativo: !categoria.ativo });
      await recarregarCategorias();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const removerCategoria = async (categoria) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Remover categoria?',
      text: `"${categoria.nome}" será apagada do catálogo. Chamados antigos continuam com a chave gravada.`,
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/atendimento/categorias/${categoria.id}`);
      await recarregarCategorias();
    } catch (err) {
      alertarErro(err, 'Não deu pra excluir');
    }
  };

  const novoSetor = async () => {
    const { value: nome } = await Swal.fire({
      title: 'Novo setor',
      input: 'text',
      inputLabel: 'Nome do setor',
      inputPlaceholder: 'Ex: Compras',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Adicionar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || v.trim().length < 2) && 'Mínimo 2 caracteres.',
    });
    if (!nome) return;
    try {
      await axios.post(`${API}/atendimento/setores`, {
        nome: nome.trim(),
        ordem: (setores.length + 1) * 10,
      });
      await recarregarSetores();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const renomearSetor = async (setor) => {
    const { value: nome } = await Swal.fire({
      title: 'Editar setor',
      input: 'text',
      inputValue: setor.nome,
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || v.trim().length < 2) && 'Mínimo 2 caracteres.',
    });
    if (!nome || nome.trim() === setor.nome) return;
    try {
      await axios.put(`${API}/atendimento/setores/${setor.id}`, { nome: nome.trim() });
      await recarregarSetores();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const toggleAtivoSetor = async (setor) => {
    try {
      await axios.put(`${API}/atendimento/setores/${setor.id}`, { ativo: !setor.ativo });
      await recarregarSetores();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    }
  };

  const removerSetor = async (setor) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Remover setor?',
      text: `"${setor.nome}" será apagado do catálogo. Chamados antigos continuam com o setor gravado.`,
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/atendimento/setores/${setor.id}`);
      await recarregarSetores();
    } catch (err) {
      alertarErro(err, 'Não deu pra excluir');
    }
  };

  const setCampo = (campo, valor) => setConfig((prev) => ({ ...prev, [campo]: valor }));

  const atualizarHorario = (dia, campo, valor) => {
    setConfig((prev) => {
      const horarios = Array.isArray(prev.horarios) ? [...prev.horarios] : [];
      const idx = horarios.findIndex((h) => Number(h.dia_semana) === dia);
      if (idx === -1) {
        horarios.push({ dia_semana: dia, ativo: false, inicio: '08:00', fim: '18:00', [campo]: valor });
      } else {
        horarios[idx] = { ...horarios[idx], [campo]: valor };
      }
      horarios.sort((a, b) => Number(a.dia_semana) - Number(b.dia_semana));
      return { ...prev, horarios };
    });
  };

  const horariosOrdenados = (() => {
    const arr = Array.isArray(config?.horarios) ? config.horarios : [];
    // Garante 7 linhas mesmo que o backend retorne incompleto
    return DIAS.map((d) => arr.find((h) => Number(h.dia_semana) === d.num)
      || { dia_semana: d.num, ativo: false, inicio: '08:00', fim: '18:00' });
  })();

  const salvar = async () => {
    setSalvando(true);
    try {
      const res = await axios.put(`${API}/atendimento/config`, config);
      setConfig(res.data?.data || config);
      Swal.fire({
        icon: 'success',
        title: 'Salvo!',
        text: 'Configuração do atendimento atualizada.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    } finally {
      setSalvando(false);
    }
  };

  if (!podeEditar) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Você não tem permissão para configurar o atendimento.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="content-shell">
        <div className="content-card content-card--centered">
          <div className="screen-loader__spinner" />
          <p>Carregando configuração...</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          {erro || 'Configuração não encontrada.'}
        </div>
      </div>
    );
  }


  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Administração</span>
          <h1>Atendimento WhatsApp</h1>
          <p>Defina horário, mensagens automáticas do bot e SLA por prioridade.</p>
        </div>
      </section>

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}

      {/* Horario */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ color: 'var(--portal-text)', marginBottom: 12 }}>
          <i className="bi bi-clock me-2" /> Horário de atendimento
        </h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 16 }}>
          Quando ativo, mensagens recebidas fora do horário não iniciam o bot - apenas
          enviam o aviso configurado abaixo. Conversas que já estavam em andamento
          continuam normalmente.
        </p>

        <div className="form-check form-switch mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="horario-ativo"
            checked={Boolean(config.horario_ativo)}
            onChange={(e) => setCampo('horario_ativo', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="horario-ativo">
            Aplicar restrição de horário
          </label>
        </div>

        <div>
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Dia</th>
                  <th style={{ width: 90 }}>Ativo</th>
                  <th>Início</th>
                  <th>Fim</th>
                </tr>
              </thead>
              <tbody>
                {horariosOrdenados.map((h, idx) => {
                  const dia = DIAS[idx];
                  return (
                    <tr key={dia.num} style={{ opacity: h.ativo ? 1 : 0.65 }}>
                      <td><strong>{dia.label}</strong></td>
                      <td>
                        <div className="form-check form-switch" style={{ marginBottom: 0 }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={Boolean(h.ativo)}
                            onChange={(e) => atualizarHorario(dia.num, 'ativo', e.target.checked)}
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          style={{ maxWidth: 140 }}
                          value={h.inicio || '08:00'}
                          onChange={(e) => atualizarHorario(dia.num, 'inicio', e.target.value)}
                          disabled={!h.ativo}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          style={{ maxWidth: 140 }}
                          value={h.fim || '18:00'}
                          onChange={(e) => atualizarHorario(dia.num, 'fim', e.target.value)}
                          disabled={!h.ativo}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginTop: 8, marginBottom: 0 }}>
            Cada dia tem horário próprio - permite, por exemplo, Seg-Sex 07:00-18:00 e Sáb 07:00-12:00.
            Este horário também define a janela de cálculo do tempo de resposta nos Indicadores:
            mensagens recebidas fora do horário não contam até o próximo período útil.
          </p>
        </div>
      </section>

      {/* Mensagens */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ color: 'var(--portal-text)', marginBottom: 12 }}>
          <i className="bi bi-chat-text me-2" /> Mensagens automáticas
        </h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 16 }}>
          Deixe em branco para usar o texto padrão. A mensagem de chamado criado aceita
          o placeholder <code>{'{{numero}}'}</code> para o id do chamado.
        </p>

        {[
          { campo: 'mensagem_saudacao', label: 'Saudação inicial' },
          { campo: 'mensagem_fora_horario', label: 'Fora do horário' },
          { campo: 'mensagem_cancelamento', label: 'Após cancelar' },
        ].map(({ campo, label }) => (
          <div key={campo} style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
              {label}
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={config[campo] || ''}
              onChange={(e) => setCampo(campo, e.target.value)}
              placeholder={PLACEHOLDERS[campo]}
            />
          </div>
        ))}
      </section>

      {/* Som */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ color: 'var(--portal-text)', marginBottom: 12 }}>
          <i className="bi bi-volume-up me-2" /> Som de nova mensagem
        </h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 16 }}>
          Configuração usada na tela de conversas quando chegam novas mensagens.
        </p>

        <div className="form-check form-switch mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="som-nova-mensagem-ativo"
            checked={config.som_nova_mensagem_ativo !== false}
            onChange={(e) => setCampo('som_nova_mensagem_ativo', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="som-nova-mensagem-ativo">
            Tocar som ao receber mensagem
          </label>
        </div>

        <div
          className="row g-3"
          style={{
            margin: 0,
            opacity: config.som_nova_mensagem_ativo === false ? 0.55 : 1,
            pointerEvents: config.som_nova_mensagem_ativo === false ? 'none' : 'auto',
          }}
        >
          <div className="col-12 col-md-4" style={{ paddingLeft: 0 }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
              Tipo
            </label>
            <select
              className="form-select"
              value={config.som_nova_mensagem_tipo || 'suave'}
              onChange={(e) => setCampo('som_nova_mensagem_tipo', e.target.value)}
            >
              {SONS_NOVA_MENSAGEM.map((s) => (
                <option key={s.chave} value={s.chave}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-5">
            <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
              Volume: {Number(config.som_nova_mensagem_volume ?? 60)}%
            </label>
            <input
              type="range"
              className="form-range"
              min={0}
              max={100}
              step={5}
              value={Number(config.som_nova_mensagem_volume ?? 60)}
              onChange={(e) => setCampo('som_nova_mensagem_volume', Number(e.target.value))}
            />
          </div>

          <div className="col-12 col-md-3" style={{ paddingRight: 0, display: 'flex', alignItems: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-info w-100"
              onClick={() => playNotificationSound({
                ativo: config.som_nova_mensagem_ativo !== false,
                tipo: config.som_nova_mensagem_tipo || 'suave',
                volume: Number(config.som_nova_mensagem_volume ?? 60),
              })}
            >
              <i className="bi bi-play-fill me-1" /> Testar
            </button>
          </div>
        </div>
      </section>

      {/* Meta de atendimento — Fio usa só um alvo (o antigo "média"). */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ color: 'var(--portal-text)', marginBottom: 4 }}>
          <i className="bi bi-stopwatch me-2" /> Meta de atendimento
        </h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 16 }}>
          O tempo-alvo (em minutos) que alimenta o <strong>"% dentro da meta"</strong> dos Indicadores.
          Conta só dentro do horário de atendimento.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ fontSize: '0.8rem', color: 'var(--portal-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Meta de 1ª resposta
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min={1} className="form-control form-control-sm" style={{ maxWidth: 130 }}
                value={config.sla_media_ttfr_min || 0} onChange={(e) => setCampo('sla_media_ttfr_min', Number(e.target.value))} />
              <span className="cell-muted">min · {minutosParaTexto(config.sla_media_ttfr_min)}</span>
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: 'var(--portal-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Meta de encerramento
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min={1} className="form-control form-control-sm" style={{ maxWidth: 130 }}
                value={config.sla_media_ttr_min || 0} onChange={(e) => setCampo('sla_media_ttr_min', Number(e.target.value))} />
              <span className="cell-muted">min · {minutosParaTexto(config.sla_media_ttr_min)}</span>
            </div>
          </div>
        </div>
      </section>


      {/* Filiais — somente leitura, fonte: view dbo.FILIAIS em BDCENTRAO */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ color: 'var(--portal-text)', marginBottom: 8 }}>
          <i className="bi bi-buildings me-2" /> Filiais do bot
        </h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 12 }}>
          Carregadas automaticamente da view <code>dbo.FILIAIS</code> em BDCENTRAO.
          Não é possível inserir ou editar filiais por aqui — qualquer alteração deve ser feita diretamente na view/ERP.
          O bot exibe o código da filial ao usuário no intake.
        </p>

        {filiaisErro ? (
          <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-triangle me-2" />
            {filiaisErro}
          </div>
        ) : filiais.length === 0 ? (
          <div style={{ padding: 12, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
            Nenhuma filial encontrada na view dbo.FILIAIS.
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Código</th>
                  <th>Nome</th>
                </tr>
              </thead>
              <tbody>
                {filiais.map((f) => (
                  <tr key={f.codigo || f.nome}>
                    <td className="cell-muted">
                      <span style={{ fontFamily: 'monospace' }}>{f.codigo || '—'}</span>
                    </td>
                    <td>{f.nome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Filas de atendimento — modo de trabalho (fio_setor) */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
          <h4 style={{ color: 'var(--portal-text)', marginBottom: 0 }}>
            <i className="bi bi-people-fill me-2" /> Filas de atendimento — como cada uma trabalha
          </h4>
          <button type="button" className="btn btn-sm" onClick={criarFila} style={{ background: '#3D8B37', color: '#fff', border: 'none' }}>
            <i className="bi bi-plus-lg me-1" /> Criar fila
          </button>
        </div>
        <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
          Define, por fila, <strong>quem vê</strong> as conversas e <strong>como</strong> uma conversa nova é entregue.
          O padrão (Colaborativo + Cada um pega) mantém o comportamento de hoje — mude só o que precisar.
        </p>
        {filas.length === 0 ? (
          <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Nenhuma fila cadastrada.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {filas.map((f) => {
              const selStyle = { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem' };
              const lblStyle = { fontSize: '0.75rem', color: 'var(--portal-muted)', fontWeight: 600, display: 'block', marginBottom: 4 };
              const hintStyle = { fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 4, lineHeight: 1.35 };
              return (
                <div key={f.id} style={{ border: '1px solid var(--portal-border)', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <i className="bi bi-inboxes" style={{ color: '#3D8B37' }} />
                    <strong style={{ fontSize: '0.95rem' }}>{f.nome}</strong>
                    <span
                      title="Canal (número) a que esta fila pertence"
                      style={{
                        fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 9,
                        border: '1px solid var(--portal-border)', color: 'var(--portal-muted)',
                      }}
                    >
                      <i className="bi bi-phone me-1" />
                      {f.canal_nome || canais.find((c) => c.id === f.canal_id)?.nome || 'sem canal'}
                    </span>
                    {/* Fila sem função não quebra nada — só não entrega
                        ferramenta nenhuma. Como é um erro SILENCIOSO, ele
                        precisa ficar visível aqui. */}
                    {!f.funcao_id && (
                      <span
                        title="Sem função definida: quem trabalha nesta fila não recebe as ferramentas do trabalho (ex.: consultar cliente)."
                        style={{
                          fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 9,
                          border: '1px solid #F5A623', color: '#F5A623',
                        }}
                      >
                        <i className="bi bi-exclamation-triangle me-1" />
                        sem função
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                    <div>
                      <label style={lblStyle}>O que essa fila faz (função)</label>
                      <select
                        style={selStyle}
                        value={f.funcao_id || ''}
                        onChange={(e) => atualizarFilaLocal(f.id, { funcao_id: e.target.value ? Number(e.target.value) : null })}
                      >
                        <option value="">— sem função —</option>
                        {funcoes.map((fn) => <option key={fn.id} value={fn.id}>{fn.nome}</option>)}
                      </select>
                      <div style={hintStyle}>
                        {(() => {
                          const fn = funcoes.find((x) => x.id === Number(f.funcao_id));
                          if (!fn) {
                            return 'Escolha o que essa fila faz. É a função — não o nome — que liga a fila de uma loja com a mesma fila da outra e libera as ferramentas.';
                          }
                          const nomes = fn.permissoes
                            .map((c) => permsFuncao.find((p) => p.chave === c)?.nome || c);
                          return nomes.length
                            ? `Quem trabalha aqui ganha: ${nomes.join(', ')}.`
                            : `"${fn.nome}" ainda não libera ferramenta nenhuma — defina abaixo, em Funções das filas.`;
                        })()}
                      </div>
                    </div>
                    <div>
                      <label style={lblStyle}>Quem vê as conversas</label>
                      <select style={selStyle} value={f.visibilidade || 'colaborativo'} onChange={(e) => atualizarFilaLocal(f.id, { visibilidade: e.target.value })}>
                        <option value="colaborativo">Colaborativo — o time vê e pega tudo</option>
                        <option value="privado">Privado — cada um só o seu + pendente</option>
                      </select>
                      <div style={hintStyle}>
                        {f.visibilidade === 'privado'
                          ? 'Ex.: Vendas. O vendedor só vê o que é dele; só o gestor vê tudo.'
                          : 'Ex.: Caixa. Um enxerga e cobre o outro.'}
                      </div>
                    </div>
                    <div>
                      <label style={lblStyle}>Distribuição da conversa nova</label>
                      <select style={selStyle} value={f.distribuicao || 'self'} onChange={(e) => atualizarFilaLocal(f.id, { distribuicao: e.target.value })}>
                        <option value="self">Cada um pega — fica no Pendente</option>
                        <option value="auto">Automática — pro disponível menos carregado</option>
                        <option value="hibrido">Híbrida — automática; se ninguém livre, Pendente</option>
                      </select>
                      <div style={hintStyle}>
                        {f.distribuicao === 'auto' && 'Já cai na fila de um atendente disponível. Ninguém livre → fica no Pendente.'}
                        {f.distribuicao === 'hibrido' && 'Distribui pros disponíveis e usa o Pendente como reserva.'}
                        {(!f.distribuicao || f.distribuicao === 'self') && 'Toda conversa fica no Pendente e o time se serve.'}
                      </div>
                    </div>
                    <div>
                      <label style={lblStyle}>Teto por atendente (opcional)</label>
                      <input type="number" min="1" style={selStyle} value={f.teto_atendente || ''} placeholder="sem limite"
                        onChange={(e) => atualizarFilaLocal(f.id, { teto_atendente: e.target.value })} />
                      <div style={hintStyle}>Máx. de conversas ativas antes de sobrar pro Pendente. Vazio = sem limite.</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <button type="button" className="btn btn-sm" disabled={filaSalvando === f.id}
                      onClick={() => salvarModoFila(f)}
                      style={{ background: '#3D8B37', color: '#fff', border: 'none' }}>
                      {filaSalvando === f.id ? 'Salvando...' : 'Salvar fila'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>


      {/* Funções das filas — o significado compartilhado entre lojas */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
          <h4 style={{ color: 'var(--portal-text)', marginBottom: 0 }}>
            <i className="bi bi-tools me-2" /> Funções das filas — que ferramenta cada função libera
          </h4>
          <button type="button" className="btn btn-sm" onClick={criarFuncao} style={{ background: '#3D8B37', color: '#fff', border: 'none' }}>
            <i className="bi bi-plus-lg me-1" /> Nova função
          </button>
        </div>
        <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
          <strong>Perfil</strong> diz o que a pessoa <strong>pode</strong> (atender, administrar) e é escolhido no
          cadastro dela. <strong>Função</strong> diz o que a fila <strong>faz</strong> e entrega a ferramenta daquele
          trabalho pra quem está nela. São coisas separadas de propósito: a mesma pessoa pode ser caixa e crediarista
          ao mesmo tempo — ela cabe em duas filas, mas tem um perfil só.
          <br />
          Marcar aqui vale pra <strong>todas as lojas</strong> de uma vez: a fila de Crediário de Carangola e a de
          Espera Feliz apontam pra mesma função, então basta marcar uma vez. Loja nova só escolhe a função e já nasce
          certa.
        </p>
        {funcoes.length === 0 ? (
          <div className="cell-muted" style={{ fontSize: '0.85rem' }}>Nenhuma função cadastrada.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {funcoes.map((fn) => (
              <div key={fn.id} style={{ border: '1px solid var(--portal-border)', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <i className="bi bi-tag" style={{ color: '#3D8B37' }} />
                  <strong style={{ fontSize: '0.95rem' }}>{fn.nome}</strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                    {fn.filas === 0
                      ? 'nenhuma fila usa esta função ainda'
                      : `${fn.filas} ${fn.filas === 1 ? 'fila usa' : 'filas usam'} esta função`}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {permsFuncao.map((p) => {
                    const marcado = fn.permissoes.includes(p.chave);
                    return (
                      <button
                        key={p.chave}
                        type="button"
                        onClick={() => alternarPermFuncao(fn.id, p.chave)}
                        title={p.chave}
                        style={{
                          background: marcado ? '#3D8B37' : 'transparent',
                          color: marcado ? '#fff' : 'var(--portal-text)',
                          border: `1px solid ${marcado ? '#3D8B37' : 'var(--portal-border)'}`,
                          borderRadius: 20, padding: '4px 12px', fontSize: '0.8rem', cursor: 'pointer',
                        }}
                      >
                        <i className={`bi ${marcado ? 'bi-check2' : 'bi-plus'} me-1`} />
                        {p.nome}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 8, lineHeight: 1.35 }}>
                  Administração e alcance entre lojas não aparecem aqui de propósito — isso é poder, vem do perfil,
                  e é conferido pessoa a pessoa.
                </div>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <button type="button" className="btn btn-sm" disabled={funcaoSalvando === fn.id}
                    onClick={() => salvarFuncao(fn)}
                    style={{ background: '#3D8B37', color: '#fff', border: 'none' }}>
                    {funcaoSalvando === fn.id ? 'Salvando...' : 'Salvar função'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modos de encerramento */}
      <section className="content-card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h4 style={{ color: 'var(--portal-text)', marginBottom: 0 }}>
            <i className="bi bi-flag me-2" /> Modos de encerramento
          </h4>
          <button
            type="button"
            className="btn btn-sm"
            onClick={abrirNovoModo}
            style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-lg me-1" /> Novo modo
          </button>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', marginBottom: 12 }}>
          O atendente escolhe um modo ao encerrar a conversa. A mensagem configurada
          é enviada automaticamente ao cliente.
        </p>

        {/* Liga/desliga o passo do motivo. Desligado, Encerrar é 1 clique. */}
        <label
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14,
            padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
            background: 'var(--portal-surface-soft)', border: '1px solid var(--portal-border)',
          }}
        >
          <input
            type="checkbox"
            checked={config?.exigir_motivo_encerramento === true}
            onChange={(e) => setCampo('exigir_motivo_encerramento', e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: '0.88rem' }}>
            Pedir o motivo ao encerrar
            <br />
            <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
              {config?.exigir_motivo_encerramento
                ? 'Ligado: ao clicar em Encerrar abre a lista de modos acima.'
                : 'Desligado: Encerrar fecha a conversa na hora, em um clique. Os modos ficam cadastrados aqui, sem serem pedidos.'}
            </span>
          </span>
        </label>

        {motivosErro ? (
          <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-triangle me-2" />
            {motivosErro}
          </div>
        ) : motivos.length === 0 ? (
          <div style={{ padding: 12, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
            Nenhum modo cadastrado.
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Mensagem ao cliente</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 160, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {motivos.map((m) => (
                  <tr key={m.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ opacity: m.ativo ? 1 : 0.55 }}>{m.descricao}</span>
                    </td>
                    <td>
                      {m.mensagem_cliente ? (
                        <span style={{ fontSize: '0.82rem', color: 'var(--portal-muted)', opacity: m.ativo ? 1 : 0.55 }}>
                          {String(m.mensagem_cliente).slice(0, 80)}{m.mensagem_cliente.length > 80 ? '…' : ''}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', opacity: 0.5, fontStyle: 'italic' }}>
                          (mensagem padrão)
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="permissao-chip" style={{
                        background: m.ativo ? 'rgba(76,181,83,0.15)' : 'rgba(94,122,130,0.15)',
                        color: m.ativo ? '#4CB553' : '#5E7A82',
                        border: `1px solid ${m.ativo ? '#4CB55355' : '#5E7A8255'}`,
                      }}>
                        {m.ativo ? 'ativo' : 'inativo'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => abrirEditarModo(m)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-info"
                          onClick={() => toggleAtivoMotivo(m)}
                          title={m.ativo ? 'Desativar' : 'Ativar'}
                        >
                          <i className={`bi ${m.ativo ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removerMotivo(m)}
                          title="Remover"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Modal criar/editar modo de encerramento */}
      {modoModal.aberto && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
            <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
              <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
                <h5 className="modal-title">
                  <i className="bi bi-flag me-2" />
                  {modoModal.editando ? 'Editar modo de encerramento' : 'Novo modo de encerramento'}
                </h5>
                <button className="btn-close btn-close-white" onClick={fecharModoModal} disabled={modoSalvando} />
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 6 }}>
                    Nome <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={modoNome}
                    onChange={(e) => setModoNome(e.target.value)}
                    placeholder="Ex: Resolvido, Encaminhado, Sem solução"
                    autoFocus
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 6 }}>
                    Mensagem ao cliente (enviada via WhatsApp ao encerrar)
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={modoMensagem}
                    onChange={(e) => setModoMensagem(e.target.value)}
                    placeholder={'Seu atendimento foi encerrado.\n\nSe precisar de mais alguma coisa, é só mandar uma mensagem.'}
                  />
                  <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', marginTop: 6 }}>
                    Deixe em branco para usar a mensagem padrão genérica.
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
                <button className="btn btn-outline-secondary" onClick={fecharModoModal} disabled={modoSalvando}>
                  Cancelar
                </button>
                <button
                  className="btn"
                  onClick={salvarModo}
                  disabled={modoSalvando || modoNome.trim().length < 3}
                  style={{ background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)', color: '#fff', fontWeight: 600, border: 'none', padding: '6px 18px' }}
                >
                  {modoSalvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          type="button"
          className="btn"
          onClick={salvar}
          disabled={salvando}
          style={{
            background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
            color: '#fff', fontWeight: 600, border: 'none', padding: '8px 22px',
          }}
        >
          {salvando ? 'Salvando...' : <><i className="bi bi-check2-circle me-2" />Salvar configuração</>}
        </button>
      </div>
    </div>
  );
}
